<template>
  <h2 class="is-size-2 mb-4 mt-2 title">{{ title }}</h2>
  <form @submit.prevent="handleSubmit" class="container m-0" novalidate>
    <div class="friendlier-field mt-2">
      <div class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="signature" />
        </span>
        <input
          id="firstName"
          v-model="fname"
          type="text"
          required
          :placeholder="$t('completeSignup.registerForm.firstName')"
          :class="{ 'mb-2': !firstNameError }"
          class="is-size-4 is-right p-2"
          @blur="validateFirstName"
        />
      </div>
      <div v-if="firstNameError" class="mt-2 has-text-danger error">{{ firstNameError }}</div>
    </div>

    <div class="friendlier-field mt-2">
      <div class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="signature" />
        </span>
        <input
          id="lastName"
          v-model="lname"
          type="text"
          required
          :placeholder="$t('completeSignup.registerForm.lastName')"
          :class="{ 'mb-2': !lastNameError }"
          class="is-size-4 is-right p-2"
          @blur="validateLastName"
        />
      </div>
      <div v-if="lastNameError" class="mt-2 has-text-danger error">{{ lastNameError }}</div>
    </div>

    <div v-if="showEmail" class="friendlier-field mt-2">
      <div class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="envelope" />
        </span>
        <input
          id="email"
          name="email"
          v-model="email"
          type="email"
          required
          :placeholder="$t('completeSignup.registerForm.email')"
          autocomplete="email"
          inputmode="email"
          autocapitalize="off"
          spellcheck="false"
          :class="{ 'mb-2': !emailError }"
          class="is-size-4 is-right p-2"
          @input="handleEmailInput"
          @blur="handleEmailBlur"
        />
      </div>
      <p v-if="emailSuggestion" class="email-suggestion">
        Did you mean
        <button type="button" class="email-suggestion-link" @click="applyEmailSuggestion">{{ emailSuggestion }}</button>?
      </p>
      <div v-if="emailError" class="mt-2 has-text-danger error">{{ emailError }}</div>
    </div>

    <div class="friendlier-field mt-2">
      <div class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="key" />
        </span>
        <input
          id="password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          required
          :placeholder="$t('completeSignup.registerForm.password')"
          :class="{ 'mb-2': !passwordError }"
          class="is-size-4 is-right p-2 password-input"
          @blur="validatePassword"
        />
        <span @click="toggleVisibility" class="password-icon"> <!-- add a span with eye icon that will call toggleVisibility @click-->
          <FontAwesomeIcon :icon="showPassword ? 'eye' : 'eye-slash'" />
        </span>
      </div>
      <div v-if="passwordError" class="mt-2 has-text-danger error">{{ passwordError }}</div>
    </div>

    <div class="friendlier-field mt-2">
      <div class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="key" />
        </span>
        <input
          id="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          v-model="confPassword"
          required
          :placeholder="$t('completeSignup.registerForm.confirm-password')"
          :class="{ 'mb-2': !confPasswordError }"
          class="is-size-4 is-right p-2 password-input"
          @blur="validateConfirmPassword"
        />
        <span @click="toggleConfirmVisibility" class="password-icon"> <!-- add a span with eye icon that will call toggleVisibility @click-->
          <FontAwesomeIcon :icon="showConfirmPassword ? 'eye' : 'eye-slash'" />
        </span>
      </div>
      <div v-if="confPasswordError" class="mt-2 has-text-danger error">{{ confPasswordError }}</div>
    </div>
    <button type="submit" :disabled="isFormInvalid || locked" class="ocean-button is-bold fs-1-2">
      {{ buttonText }}
    </button>
    <div v-if="error" class="mt-4 has-text-danger error">{{ error }}</div>
  </form>
</template>
<script setup>
import { ref, watch, computed } from "vue";
import { createValidator, getSuggestedEmail } from "@/revamp/utils";
import { useI18n } from "vue-i18n";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { t } = useI18n();
const validators = createValidator(t);

const props = defineProps({
  showEmail: {
    type: Boolean,
    default: false,
  },
  locked: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  buttonText: {
    type: String,
    default: "Submit",
  },
  emailVal: {
    type: String,
    default: "",
  },
  submit: {
    type: Function,
    required: true,
  },
});

// Form Fields
const email = ref(props.emailVal || "");
const fname = ref("");
const lname = ref("");
const password = ref("");
const confPassword = ref("");

// Error States
const firstNameError = ref("");
const lastNameError = ref("");
const emailError = ref("");
const emailSuggestion = ref(null);
const passwordError = ref("");
const confPasswordError = ref("");
const error = ref("");

const showPassword = ref(false); //default to hiding
const showConfirmPassword = ref(false); //default to hiding

function toggleVisibility() {
  showPassword.value = !showPassword.value;
}

function toggleConfirmVisibility() {
  showConfirmPassword.value = !showConfirmPassword.value;
}

// Computed properties
const isFormInvalid = computed(() => {
  const hasRequiredFields = fname.value.trim() && lname.value.trim() && password.value && confPassword.value;

  const hasValidEmail = !props.showEmail || (email.value.trim() && !emailError.value);

  const hasErrors =
    emailError.value || firstNameError.value || lastNameError.value || passwordError.value || confPasswordError.value;

  return !hasRequiredFields || !hasValidEmail || hasErrors;
});

// Watchers
watch(email, () => {
  emailSuggestion.value = null;
  error.value = "";
  if (props.showEmail) {
    validateEmail();
  }
});

const handleEmailInput = () => {
  emailSuggestion.value = null;
};

const handleEmailBlur = () => {
  emailSuggestion.value = getSuggestedEmail(email.value);
  validateEmail();
};

const applyEmailSuggestion = () => {
  if (!emailSuggestion.value) return;
  email.value = emailSuggestion.value;
  emailSuggestion.value = null;
  validateEmail();
};

watch(fname, () => {
  error.value = "";
  validateFirstName();
});

watch(lname, () => {
  error.value = "";
  validateLastName();
});

watch(password, () => {
  error.value = "";
  validatePassword();
});

watch(confPassword, () => {
  error.value = "";
  validateConfirmPassword();
});

const validateFirstName = () => {
  firstNameError.value = fname.value.trim() ? "" : t("completeSignup.registerForm.validFirstName");
};

const validateLastName = () => {
  lastNameError.value = lname.value.trim() ? "" : t("completeSignup.registerForm.validLastName");
};

const validateEmail = () => {
  error.value = "";
  emailError.value = "";
  if (!email.value.trim()) {
    emailError.value = "";
    return;
  }
  if (!validators.email(email.value)) {
    emailError.value = t("completeSignup.registerForm.validEmail");
  }
};

const validatePassword = () => {
  passwordError.value = validators.password(password.value);
  if (confPassword.value) {
    validateConfirmPassword();
  }
};

const validateConfirmPassword = () => {
  confPasswordError.value = validators.confirmPassword(password.value, confPassword.value);
};

// Form submission
const handleSubmit = async () => {
  // Clear previous errors
  error.value = "";

  // Validate all fields
  validateFirstName();
  validateLastName();
  if (props.showEmail) {
    validateEmail();
  }
  validatePassword();
  validateConfirmPassword();

  if (isFormInvalid.value) {
    error.value = t("completeSignup.registerForm.fix");
    return;
  }

  try {
    await props.submit({
      fname: fname.value.trim(),
      lname: lname.value.trim(),
      password: password.value,
      ...(props.showEmail ? { userEmail: email.value.trim().toLowerCase() } : {}),
    });
  } catch (err) {
    error.value = t("claimContainer.oops-err");
    console.error("Form submission error:", err);
  }
};
</script>
<style lang="scss" scoped>
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";

.title {
  color: $sunflower;
}

.password-input {
  padding-right: 3rem !important; //this adds padding only to the password input
}

.password-icon {
  position: absolute;
  right: 0.75rem; //0.75rem away from right 
  top: 50%; 
  transform: translateY(-50%); //this and top will center it vertically
  color: #f0ba30; //from main.scss
  cursor: pointer; //indicate clickable
}

.control input {
  width: 100%; //email and password are both friendlier-field inputs so to keep them the same length just make their width 100% explicitly
  box-sizing: border-box; //makes it so that when its calculating width it includes padding
}

span.icon {
  width: 20px;
}

.email-suggestion {
  margin-top: 0.5rem;
  color: #4a4a4a;
}

.email-suggestion-link {
  appearance: none;
  border: none;
  background: none;
  color: #0048b4;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}
</style>